import { permissionsByEntity } from '@cmg/auth';

export enum PermissionLevel {
  READ = 'read',
  FULL = 'full',
}

export enum GroupName {
  Administration = 'Administration',
  CRM = 'CRM',
  Offerings = 'Offerings',
  Datalab = 'Datalab',
  OrderBook = 'Order Book',
  RegulatoryFilings = 'Regulatory Filings',
  SyndicateWires = 'Syndicate Wires',
  Settlement = 'Settlement',
  Firms = 'Firms',
  TradeRelease = 'Trade Release',
  SalesCredit = 'Sales Credit',
  WallCross = 'Wall Cross',
}

export const EntityDisplay: Record<keyof typeof permissionsByEntity, string> = {
  GlobalAccount: 'Global Account',
  GlobalUser: 'Global User',
  GlobalRole: 'Global Role',
  GlobalApiKey: 'Global Api Key',
  ApiKey: 'Api Key',
  Firm: 'Firm',
  Account: 'Account',
  User: 'User',
  Role: 'Role',
  Team: 'Team',
  SignupToken: 'Signup Token',
  Conflict: 'Conflict',
  Contact: 'Contact',
  Offering: 'Offering',
  PublishedOffering: 'Published Offering',
  OfferingActivity: 'Offering Activity',
  DataSynchronization: 'Data Synchronization',
  Ioi: 'Ioi',
  FundIoi: 'Fund Ioi',
  CustomSectors: 'Custom Sectors',
  ApiDocs: 'Api Docs',
  SharedReports: 'Shared Reports',
  ParticipationReport: 'Participation Report',
  ParticipationSet: 'Participation Set',
  GlobalParticipationSet: 'Global Participation Set',
  SharedFollowedOfferings: 'Shared Followed Offerings',
  OrderBook: 'Order Book',
  InstitutionalIndication: 'Institutional Indications',
  InstitutionalAllocation: 'Institutional Allocations',
  RetailDemand: 'Retail Demand',
  RetailRetention: 'Retail Retentions',
  CoveredAccountsCrm: 'CRM Covered Accounts',
  CoveredAccountsInstitutionalIndication: 'Covered Accounts Institutional Indication',
  CoveredAccountsInstitutionalAllocation: 'Covered Accounts Institutional Allocation',
  RegulatoryFiling: 'Regulatory Filing',
  SyndicateWire: 'Syndicate Wire',
  Datalab: 'Datalab',
  DatalabUI: 'Datalab UI',
  CRM: 'CRM',
  CrmBasicInvestorFirm: 'CRM Basic Investor Firm',
  Company: 'Company',
  SystemDataLab: 'System DataLab',
  WallCross: 'Wall Cross',
  /** @deprecated OffPlatformOffering is deprecated, use `SystemDataLab` instead */
  OffPlatformOffering: 'System DataLab',
  InternationalOffering: 'International Offering',
  GlobalCrm: 'Global CRM',
  GlobalInstitutionalIndication: 'Global Institutional Indication',
  Impersonation: 'Impersonation',
  Underwriter: 'Underwriter',
  FullSyndicateLineUp: 'Full Syndicate Line Up',
  Designation: 'Designation',
  Expense: 'Expense',
  Stabilization: 'Stabilization',
  Accounting: 'Accounting',
  Letter: 'Letter',
  Trade: 'Trades',
  SalesCredit: 'Sales Credit',
  SyndicateMember: 'Syndicate Member',
  CertificateLibrary: 'Certificate Library',
  CertificateReview: 'Certificate Review',
  ATM: 'ATM',
  Converts: 'Converts',
  ATMSelldown: 'ATM Selldown',
  DLExcelDownload: 'DL Excel Download',
  HangfireDashboard: 'Hangfire Dashboard',
  ConvertsOffering: 'Converts Offering',
  AccountRemapping: 'Account Remapping',
  PrivateFunding: 'Private Funding',
  InternationalConvertsOffering: 'International Converts Offering',
  PIPE: 'PIPE',
  AdminMdl: 'Admin MDL',
  UnderwritingTerms: 'Underwriting Terms',
  IpoFundamentals: 'IPO Fundamentals',
  Investor: 'Investor',
  ViewSetting: 'View Setting',
};

export type AccessLevel = PermissionLevel.READ | PermissionLevel.FULL;

export type PermissionDetail = {
  groupName: GroupName;
  permissionName: string;
  permissionLevel: PermissionLevel;
  name: string;
  entity: (typeof EntityDisplay)[keyof typeof EntityDisplay];
};

export type UndefinedPermissionDetail = {
  groupName: string;
  permissionName: string;
  permissionLevel: string;
  name: string;
  entity: string;
};

export const permissionDisplay: Record<string, PermissionDetail> = {
  [permissionsByEntity.GlobalAccount.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Global Accounts',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.GlobalAccount.READ,
    entity: EntityDisplay.GlobalAccount,
  },
  [permissionsByEntity.GlobalAccount.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Global Accounts',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.GlobalAccount.FULL,
    entity: EntityDisplay.GlobalAccount,
  },
  [permissionsByEntity.GlobalUser.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Global Users',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.GlobalUser.READ,
    entity: EntityDisplay.GlobalUser,
  },
  [permissionsByEntity.GlobalUser.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Global Users',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.GlobalUser.FULL,
    entity: EntityDisplay.GlobalUser,
  },
  [permissionsByEntity.GlobalRole.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Global Roles',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.GlobalRole.READ,
    entity: EntityDisplay.GlobalRole,
  },
  [permissionsByEntity.GlobalRole.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Global Roles',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.GlobalRole.FULL,
    entity: EntityDisplay.GlobalRole,
  },
  [permissionsByEntity.SignupToken.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Signup Token',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.SignupToken.READ,
    entity: EntityDisplay.SignupToken,
  },
  [permissionsByEntity.SignupToken.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Signup Token',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.SignupToken.FULL,
    entity: EntityDisplay.SignupToken,
  },
  [permissionsByEntity.GlobalApiKey.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Global Api Keys',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.GlobalApiKey.READ,
    entity: EntityDisplay.GlobalApiKey,
  },
  [permissionsByEntity.GlobalApiKey.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Global Api Keys',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.GlobalApiKey.FULL,
    entity: EntityDisplay.GlobalApiKey,
  },
  [permissionsByEntity.ApiKey.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Api Keys',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.ApiKey.READ,
    entity: EntityDisplay.ApiKey,
  },
  [permissionsByEntity.ApiKey.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Api Keys',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.ApiKey.FULL,
    entity: EntityDisplay.ApiKey,
  },
  [permissionsByEntity.Firm.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Firms',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Firm.READ,
    entity: EntityDisplay.Firm,
  },
  [permissionsByEntity.Firm.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Firms',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Firm.FULL,
    entity: EntityDisplay.Firm,
  },
  [permissionsByEntity.Account.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Accounts',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Account.READ,
    entity: EntityDisplay.Account,
  },
  [permissionsByEntity.Account.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Accounts',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Account.FULL,
    entity: EntityDisplay.Account,
  },
  [permissionsByEntity.User.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Users',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.User.READ,
    entity: EntityDisplay.User,
  },
  [permissionsByEntity.User.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Users',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.User.FULL,
    entity: EntityDisplay.User,
  },
  [permissionsByEntity.Role.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Roles',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Role.READ,
    entity: EntityDisplay.Role,
  },
  [permissionsByEntity.Role.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Roles',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Role.FULL,
    entity: EntityDisplay.Role,
  },
  [permissionsByEntity.Team.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Teams',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Team.READ,
    entity: EntityDisplay.Team,
  },
  [permissionsByEntity.Team.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Teams',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Team.FULL,
    entity: EntityDisplay.Team,
  },
  [permissionsByEntity.Conflict.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Conflicts',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Conflict.READ,
    entity: EntityDisplay.Conflict,
  },
  [permissionsByEntity.Conflict.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Conflicts',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Conflict.FULL,
    entity: EntityDisplay.Conflict,
  },
  [permissionsByEntity.Contact.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Contacts',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Contact.READ,
    entity: EntityDisplay.Contact,
  },
  [permissionsByEntity.Contact.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Contacts',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Contact.FULL,
    entity: EntityDisplay.Contact,
  },
  [permissionsByEntity.GlobalInstitutionalIndication.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Global Institutional Indication',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.GlobalInstitutionalIndication.READ,
    entity: EntityDisplay.GlobalInstitutionalIndication,
  },
  [permissionsByEntity.GlobalInstitutionalIndication.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Global Institutional Indication',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.GlobalInstitutionalIndication.FULL,
    entity: EntityDisplay.GlobalInstitutionalIndication,
  },
  [permissionsByEntity.GlobalCrm.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Global CRM',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.GlobalCrm.READ,
    entity: EntityDisplay.GlobalCrm,
  },
  [permissionsByEntity.GlobalCrm.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Global CRM',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.GlobalCrm.FULL,
    entity: EntityDisplay.GlobalCrm,
  },
  [permissionsByEntity.Impersonation.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Impersonation',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Impersonation.READ,
    entity: EntityDisplay.Impersonation,
  },
  [permissionsByEntity.Impersonation.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Impersonation',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Impersonation.FULL,
    entity: EntityDisplay.Impersonation,
  },
  [permissionsByEntity.Offering.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View Offerings',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Offering.READ,
    entity: EntityDisplay.Offering,
  },
  [permissionsByEntity.Offering.FULL]: {
    groupName: GroupName.Offerings,
    permissionName: 'Manage Offerings',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Offering.FULL,
    entity: EntityDisplay.Offering,
  },
  [permissionsByEntity.PublishedOffering.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View Published Offerings',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.PublishedOffering.READ,
    entity: EntityDisplay.PublishedOffering,
  },
  [permissionsByEntity.PublishedOffering.FULL]: {
    groupName: GroupName.Offerings,
    permissionName: 'Manage Published Offerings',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.PublishedOffering.FULL,
    entity: EntityDisplay.PublishedOffering,
  },
  [permissionsByEntity.OfferingActivity.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Offering Activity',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.OfferingActivity.READ,
    entity: EntityDisplay.OfferingActivity,
  },
  [permissionsByEntity.OfferingActivity.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Offering Activity',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.OfferingActivity.FULL,
    entity: EntityDisplay.OfferingActivity,
  },
  [permissionsByEntity.DataSynchronization.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Data Synchronization',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.DataSynchronization.READ,
    entity: EntityDisplay.DataSynchronization,
  },
  [permissionsByEntity.DataSynchronization.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Data Synchronization',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.DataSynchronization.FULL,
    entity: EntityDisplay.DataSynchronization,
  },
  [permissionsByEntity.SystemDataLab.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View Off Platform Offering Admin',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.SystemDataLab.READ,
    entity: EntityDisplay.SystemDataLab,
  },
  [permissionsByEntity.SystemDataLab.FULL]: {
    groupName: GroupName.Offerings,
    permissionName: 'Manage Off Platform Offering Admin',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.SystemDataLab.FULL,
    entity: EntityDisplay.SystemDataLab,
  },
  [permissionsByEntity.FullSyndicateLineUp.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View Full Syndicate Line up',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.FullSyndicateLineUp.READ,
    entity: EntityDisplay.FullSyndicateLineUp,
  },
  [permissionsByEntity.InternationalOffering.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View International Offering',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.InternationalOffering.READ,
    entity: EntityDisplay.InternationalOffering,
  },
  [permissionsByEntity.ATM.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View ATM',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.ATM.READ,
    entity: EntityDisplay.ATM,
  },
  [permissionsByEntity.Converts.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View Converts',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Converts.READ,
    entity: EntityDisplay.Converts,
  },
  [permissionsByEntity.ATMSelldown.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View ATM Selldown',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.ATMSelldown.READ,
    entity: EntityDisplay.ATMSelldown,
  },
  [permissionsByEntity.Ioi.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View IOIs and Allocations',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Ioi.READ,
    entity: EntityDisplay.Ioi,
  },
  [permissionsByEntity.Ioi.FULL]: {
    groupName: GroupName.Datalab,
    permissionName: 'Manage IOIs and Allocations',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Ioi.FULL,
    entity: EntityDisplay.Ioi,
  },
  [permissionsByEntity.FundIoi.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View Fund IOIs and Allocations',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.FundIoi.READ,
    entity: EntityDisplay.FundIoi,
  },
  [permissionsByEntity.FundIoi.FULL]: {
    groupName: GroupName.Datalab,
    permissionName: 'Manage Fund IOIs and Allocations',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.FundIoi.FULL,
    entity: EntityDisplay.FundIoi,
  },
  [permissionsByEntity.Datalab.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View DataLab',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Datalab.READ,
    entity: EntityDisplay.Datalab,
  },
  [permissionsByEntity.DatalabUI.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View DataLab UI',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.DatalabUI.READ,
    entity: EntityDisplay.DatalabUI,
  },
  [permissionsByEntity.CustomSectors.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View Custom Sectors',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.CustomSectors.READ,
    entity: EntityDisplay.CustomSectors,
  },
  [permissionsByEntity.CustomSectors.FULL]: {
    groupName: GroupName.Datalab,
    permissionName: 'Manage Custom Sectors',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.CustomSectors.FULL,
    entity: EntityDisplay.CustomSectors,
  },
  [permissionsByEntity.ApiDocs.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View Api Docs',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.ApiDocs.READ,
    entity: EntityDisplay.ApiDocs,
  },
  [permissionsByEntity.ParticipationReport.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'Participation Report',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.ParticipationReport.READ,
    entity: EntityDisplay.ParticipationReport,
  },
  [permissionsByEntity.ParticipationSet.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'Participation Set',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.ParticipationSet.READ,
    entity: EntityDisplay.ParticipationSet,
  },
  [permissionsByEntity.ParticipationSet.FULL]: {
    groupName: GroupName.Datalab,
    permissionName: 'Participation Set',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.ParticipationSet.FULL,
    entity: EntityDisplay.ParticipationSet,
  },
  [permissionsByEntity.GlobalParticipationSet.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'Global Participation Set',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.GlobalParticipationSet.READ,
    entity: EntityDisplay.GlobalParticipationSet,
  },
  [permissionsByEntity.GlobalParticipationSet.FULL]: {
    groupName: GroupName.Datalab,
    permissionName: 'Global Participation Set',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.GlobalParticipationSet.FULL,
    entity: EntityDisplay.GlobalParticipationSet,
  },
  [permissionsByEntity.SharedReports.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View Shared Reports',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.SharedReports.READ,
    entity: EntityDisplay.SharedReports,
  },
  [permissionsByEntity.SharedReports.FULL]: {
    groupName: GroupName.Datalab,
    permissionName: 'Manage Shared Reports',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.SharedReports.FULL,
    entity: EntityDisplay.SharedReports,
  },
  [permissionsByEntity.SharedFollowedOfferings.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View Shared Followed Offerings',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.SharedFollowedOfferings.READ,
    entity: EntityDisplay.SharedFollowedOfferings,
  },
  [permissionsByEntity.DLExcelDownload.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View Shared Followed Offerings',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.DLExcelDownload.READ,
    entity: EntityDisplay.DLExcelDownload,
  },
  [permissionsByEntity.OrderBook.FULL]: {
    groupName: GroupName.OrderBook,
    permissionName: 'Manage Order Book',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.OrderBook.FULL,
    entity: EntityDisplay.OrderBook,
  },
  [permissionsByEntity.OrderBook.READ]: {
    groupName: GroupName.OrderBook,
    permissionName: 'View Order Book',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.OrderBook.READ,
    entity: EntityDisplay.OrderBook,
  },
  [permissionsByEntity.InstitutionalIndication.READ]: {
    groupName: GroupName.OrderBook,
    permissionName: 'View Institutional Indication',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.InstitutionalIndication.READ,
    entity: EntityDisplay.InstitutionalIndication,
  },
  [permissionsByEntity.InstitutionalIndication.FULL]: {
    groupName: GroupName.OrderBook,
    permissionName: 'Manage Institutional Indication',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.InstitutionalIndication.FULL,
    entity: EntityDisplay.InstitutionalIndication,
  },
  [permissionsByEntity.InstitutionalAllocation.READ]: {
    groupName: GroupName.OrderBook,
    permissionName: 'View Institutional Allocation',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.InstitutionalAllocation.READ,
    entity: EntityDisplay.InstitutionalAllocation,
  },
  [permissionsByEntity.RetailDemand.READ]: {
    groupName: GroupName.OrderBook,
    permissionName: 'View Retail Demand',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.RetailDemand.READ,
    entity: EntityDisplay.RetailDemand,
  },
  [permissionsByEntity.RetailDemand.FULL]: {
    groupName: GroupName.OrderBook,
    permissionName: 'Manage Retail Demand',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.RetailDemand.FULL,
    entity: EntityDisplay.RetailDemand,
  },
  [permissionsByEntity.RetailRetention.READ]: {
    groupName: GroupName.OrderBook,
    permissionName: 'View Retail Retention',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.RetailRetention.READ,
    entity: EntityDisplay.RetailRetention,
  },
  [permissionsByEntity.RetailRetention.FULL]: {
    groupName: GroupName.OrderBook,
    permissionName: 'Manage Retail Retention',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.RetailRetention.FULL,
    entity: EntityDisplay.RetailRetention,
  },
  [permissionsByEntity.InstitutionalAllocation.FULL]: {
    groupName: GroupName.OrderBook,
    permissionName: 'Manage Institutional Allocation',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.InstitutionalAllocation.FULL,
    entity: EntityDisplay.InstitutionalAllocation,
  },
  [permissionsByEntity.CoveredAccountsInstitutionalIndication.READ]: {
    groupName: GroupName.OrderBook,
    permissionName: 'View Covered Accounts Institutional Indication',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.CoveredAccountsInstitutionalIndication.READ,
    entity: EntityDisplay.CoveredAccountsInstitutionalIndication,
  },
  [permissionsByEntity.CoveredAccountsInstitutionalIndication.FULL]: {
    groupName: GroupName.OrderBook,
    permissionName: 'Manage Covered Accounts Institutional Indication',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.CoveredAccountsInstitutionalIndication.FULL,
    entity: EntityDisplay.CoveredAccountsInstitutionalIndication,
  },
  [permissionsByEntity.CoveredAccountsInstitutionalAllocation.READ]: {
    groupName: GroupName.OrderBook,
    permissionName: 'View Covered Accounts Institutional Allocation',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.CoveredAccountsInstitutionalAllocation.READ,
    entity: EntityDisplay.CoveredAccountsInstitutionalAllocation,
  },
  [permissionsByEntity.CoveredAccountsInstitutionalAllocation.FULL]: {
    groupName: GroupName.OrderBook,
    permissionName: 'Manage Covered Accounts Institutional Allocation',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.CoveredAccountsInstitutionalAllocation.FULL,
    entity: EntityDisplay.CoveredAccountsInstitutionalAllocation,
  },
  [permissionsByEntity.RegulatoryFiling.READ]: {
    groupName: GroupName.RegulatoryFilings,
    permissionName: 'View Regulatory Filing',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.RegulatoryFiling.READ,
    entity: EntityDisplay.RegulatoryFiling,
  },
  [permissionsByEntity.RegulatoryFiling.FULL]: {
    groupName: GroupName.RegulatoryFilings,
    permissionName: 'Manage Regulatory Filing',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.RegulatoryFiling.FULL,
    entity: EntityDisplay.RegulatoryFiling,
  },
  [permissionsByEntity.SyndicateWire.READ]: {
    groupName: GroupName.SyndicateWires,
    permissionName: 'View Syndicate Wire',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.SyndicateWire.READ,
    entity: EntityDisplay.SyndicateWire,
  },
  [permissionsByEntity.SyndicateWire.FULL]: {
    groupName: GroupName.SyndicateWires,
    permissionName: 'Manage Syndicate Wire',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.SyndicateWire.FULL,
    entity: EntityDisplay.SyndicateWire,
  },
  [permissionsByEntity.CRM.READ]: {
    groupName: GroupName.CRM,
    permissionName: 'View CRM',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.CRM.READ,
    entity: EntityDisplay.CRM,
  },
  [permissionsByEntity.CoveredAccountsCrm.READ]: {
    groupName: GroupName.CRM,
    permissionName: 'View CRM Covered Accounts',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.CoveredAccountsCrm.READ,
    entity: EntityDisplay.CoveredAccountsCrm,
  },
  [permissionsByEntity.CRM.FULL]: {
    groupName: GroupName.CRM,
    permissionName: 'Manage CRM',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.CRM.FULL,
    entity: EntityDisplay.CRM,
  },
  [permissionsByEntity.CrmBasicInvestorFirm.READ]: {
    groupName: GroupName.CRM,
    permissionName: 'View CRM Basic Investor Firm',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.CrmBasicInvestorFirm.READ,
    entity: EntityDisplay.CrmBasicInvestorFirm,
  },
  [permissionsByEntity.CrmBasicInvestorFirm.FULL]: {
    groupName: GroupName.CRM,
    permissionName: 'Manage CRM Basic Investor Firm',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.CrmBasicInvestorFirm.FULL,
    entity: EntityDisplay.CrmBasicInvestorFirm,
  },
  [permissionsByEntity.Company.READ]: {
    groupName: GroupName.Firms,
    permissionName: 'View Company',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Company.READ,
    entity: EntityDisplay.Company,
  },
  [permissionsByEntity.Company.FULL]: {
    groupName: GroupName.Firms,
    permissionName: 'Manage Company',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Company.FULL,
    entity: EntityDisplay.Company,
  },
  [permissionsByEntity.Underwriter.READ]: {
    groupName: GroupName.Firms,
    permissionName: 'View Underwriter',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Underwriter.READ,
    entity: EntityDisplay.Underwriter,
  },
  [permissionsByEntity.Designation.READ]: {
    groupName: GroupName.Settlement,
    permissionName: 'View Designation',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Designation.READ,
    entity: EntityDisplay.Designation,
  },
  [permissionsByEntity.Designation.FULL]: {
    groupName: GroupName.Settlement,
    permissionName: 'Manage Designation',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Designation.FULL,
    entity: EntityDisplay.Designation,
  },
  [permissionsByEntity.Expense.READ]: {
    groupName: GroupName.Settlement,
    permissionName: 'View Expense',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Expense.READ,
    entity: EntityDisplay.Expense,
  },
  [permissionsByEntity.Expense.FULL]: {
    groupName: GroupName.Settlement,
    permissionName: 'Manage Expense',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Expense.FULL,
    entity: EntityDisplay.Expense,
  },
  [permissionsByEntity.Stabilization.READ]: {
    groupName: GroupName.Settlement,
    permissionName: 'View Stabilization',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Stabilization.READ,
    entity: EntityDisplay.Stabilization,
  },
  [permissionsByEntity.Stabilization.FULL]: {
    groupName: GroupName.Settlement,
    permissionName: 'Manage Stabilization',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Stabilization.FULL,
    entity: EntityDisplay.Stabilization,
  },
  [permissionsByEntity.Accounting.READ]: {
    groupName: GroupName.Settlement,
    permissionName: 'View Accounting',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Accounting.READ,
    entity: EntityDisplay.Accounting,
  },
  [permissionsByEntity.Accounting.FULL]: {
    groupName: GroupName.Settlement,
    permissionName: 'Manage Accounting',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Accounting.FULL,
    entity: EntityDisplay.Accounting,
  },
  [permissionsByEntity.Letter.READ]: {
    groupName: GroupName.Settlement,
    permissionName: 'View Letter',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Letter.READ,
    entity: EntityDisplay.Letter,
  },
  [permissionsByEntity.Letter.FULL]: {
    groupName: GroupName.Settlement,
    permissionName: 'Manage Letter',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Letter.FULL,
    entity: EntityDisplay.Letter,
  },
  [permissionsByEntity.SalesCredit.READ]: {
    groupName: GroupName.SalesCredit,
    permissionName: 'View Sales Credit',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.SalesCredit.READ,
    entity: EntityDisplay.SalesCredit,
  },
  [permissionsByEntity.SalesCredit.FULL]: {
    groupName: GroupName.SalesCredit,
    permissionName: 'Manage Sales Credit',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.SalesCredit.FULL,
    entity: EntityDisplay.SalesCredit,
  },
  [permissionsByEntity.Trade.READ]: {
    groupName: GroupName.TradeRelease,
    permissionName: 'View Trades',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.Trade.READ,
    entity: EntityDisplay.Trade,
  },
  [permissionsByEntity.Trade.FULL]: {
    groupName: GroupName.TradeRelease,
    permissionName: 'Manage Trades',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.Trade.FULL,
    entity: EntityDisplay.Trade,
  },
  [permissionsByEntity.SyndicateMember.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View Syndicate Member',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.SyndicateMember.READ,
    entity: EntityDisplay.SyndicateMember,
  },
  [permissionsByEntity.CertificateLibrary.READ]: {
    groupName: GroupName.CRM,
    permissionName: 'View Certificate Library',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.CertificateLibrary.READ,
    entity: EntityDisplay.CertificateLibrary,
  },
  [permissionsByEntity.CertificateReview.READ]: {
    groupName: GroupName.CRM,
    permissionName: 'View Certificate Review',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.CertificateReview.READ,
    entity: EntityDisplay.CertificateReview,
  },
  [permissionsByEntity.CertificateReview.FULL]: {
    groupName: GroupName.CRM,
    permissionName: 'Manage Certificate Review',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.CertificateReview.FULL,
    entity: EntityDisplay.CertificateReview,
  },
  [permissionsByEntity.WallCross.READ]: {
    groupName: GroupName.WallCross,
    permissionName: 'View Wall Cross',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.WallCross.READ,
    entity: EntityDisplay.WallCross,
  },
  [permissionsByEntity.WallCross.FULL]: {
    groupName: GroupName.WallCross,
    permissionName: 'Manage Wall Cross',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.WallCross.FULL,
    entity: EntityDisplay.WallCross,
  },
  [permissionsByEntity.HangfireDashboard.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Hangfire Dashboard',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.HangfireDashboard.READ,
    entity: EntityDisplay.HangfireDashboard,
  },
  [permissionsByEntity.HangfireDashboard.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Hangfire Dashboard',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.HangfireDashboard.FULL,
    entity: EntityDisplay.HangfireDashboard,
  },
  [permissionsByEntity.AccountRemapping.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View Account Remapping',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.AccountRemapping.READ,
    entity: EntityDisplay.AccountRemapping,
  },
  [permissionsByEntity.AccountRemapping.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage Account Remapping',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.AccountRemapping.FULL,
    entity: EntityDisplay.AccountRemapping,
  },
  [permissionsByEntity.PrivateFunding.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View Private Funding',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.PrivateFunding.READ,
    entity: EntityDisplay.PrivateFunding,
  },
  [permissionsByEntity.InternationalConvertsOffering.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View International Converts Offering',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.InternationalConvertsOffering.READ,
    entity: EntityDisplay.InternationalConvertsOffering,
  },
  [permissionsByEntity.PIPE.READ]: {
    groupName: GroupName.Offerings,
    permissionName: 'View Pipe Offerings',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.PIPE.READ,
    entity: EntityDisplay.PIPE,
  },
  [permissionsByEntity.AdminMdl.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View Admin MDL Data',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.AdminMdl.READ,
    entity: EntityDisplay.AdminMdl,
  },
  [permissionsByEntity.AdminMdl.FULL]: {
    groupName: GroupName.Datalab,
    permissionName: 'Manage Admin MDL Data',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.AdminMdl.FULL,
    entity: EntityDisplay.AdminMdl,
  },
  [permissionsByEntity.UnderwritingTerms.READ]: {
    groupName: GroupName.Settlement,
    permissionName: 'View Underwriting Terms',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.UnderwritingTerms.READ,
    entity: EntityDisplay.UnderwritingTerms,
  },
  [permissionsByEntity.UnderwritingTerms.FULL]: {
    groupName: GroupName.Settlement,
    permissionName: 'Manage Underwriting Terms',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.UnderwritingTerms.FULL,
    entity: EntityDisplay.UnderwritingTerms,
  },
  [permissionsByEntity.IpoFundamentals.READ]: {
    groupName: GroupName.Datalab,
    permissionName: 'View IPO Fundamentals',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.IpoFundamentals.READ,
    entity: EntityDisplay.IpoFundamentals,
  },
  [permissionsByEntity.IpoFundamentals.FULL]: {
    groupName: GroupName.Datalab,
    permissionName: 'Manage IPO Fundamentals',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.IpoFundamentals.FULL,
    entity: EntityDisplay.IpoFundamentals,
  },
  [permissionsByEntity.ViewSetting.READ]: {
    groupName: GroupName.Administration,
    permissionName: 'View View Setting',
    permissionLevel: PermissionLevel.READ,
    name: permissionsByEntity.ViewSetting.READ,
    entity: EntityDisplay.ViewSetting,
  },
  [permissionsByEntity.ViewSetting.FULL]: {
    groupName: GroupName.Administration,
    permissionName: 'Manage View Setting',
    permissionLevel: PermissionLevel.FULL,
    name: permissionsByEntity.ViewSetting.FULL,
    entity: EntityDisplay.ViewSetting,
  },
};
